import * as React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cell } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';

import { cn } from '../../../../lib/stylingUtils';

export type TableCellShowHShadow = 'left' | 'right' | false | true | null;

interface TableCellProps<T> {
  cell: Cell<T, unknown>;
  ref: React.RefObject<HTMLTableCellElement | null>;
  isInRange: boolean;
  showHShadow?: TableCellShowHShadow;
  isGroupRow?: boolean;
}

export const TableCell = <T,>({
  cell,
  ref,
  isInRange,
  showHShadow,
  isGroupRow,
  ...props
}: TableCellProps<T> & React.TdHTMLAttributes<HTMLTableCellElement>) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  React.useEffect(() => {
    setNodeRef(ref.current);
  }, [ref, setNodeRef]);

  const pinningStyles: React.CSSProperties = {
    width: cell.column.getSize(),
    transform: CSS.Translate.toString(transform),
    '--column-after': `${cell.column.getAfter('right')}px`,
    '--column-start': `${cell.column.getStart('left') + cell.column.getIndex()}px`,
  } as React.CSSProperties;

  // Get the original column definition from the cell
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const originalColumn = cell.column.columnDef.meta as any;

  const handleClick = React.useCallback(() => {
    if (originalColumn?.onClick) {
      originalColumn.onClick({ row: cell.row.original });
    }
  }, [originalColumn, cell.row.original]);

  const handleDoubleClick = React.useCallback(() => {
    if (originalColumn?.onDoubleClick) {
      originalColumn.onDoubleClick({ row: cell.row.original });
    }
  }, [originalColumn, cell.row.original]);

  const isPinnedLeft = cell.column.getIsPinned() === 'left';
  const isPinnedRight = cell.column.getIsPinned() === 'right';
  const isPinned = cell.column.getIsPinned() !== false;

  return (
    <td
      {...props}
      ref={ref}
      className={cn(
        '@TableCell',
        'relative border-r border-b border-neutral/10 px-2.5 py-1.5 align-middle outline-none select-none',
        'last:border-r-0',
        '[&:has([role=checkbox])]:pr-0',
        '[tr:has([data-menu-open])_&]:bg-white [tr:has([data-menu-open])_&]:before:pointer-events-none [tr:has([data-menu-open])_&]:before:absolute [tr:has([data-menu-open])_&]:before:inset-0 [tr:has([data-menu-open])_&]:before:bg-primary/10',
        isDragging && 'z-10 opacity-80',
        !isDragging && 'opacity-100!',
        cell.column.getIsResizing() && 'select-none',
        isGroupRow &&
          'bg-white before:pointer-events-none before:absolute before:inset-0 before:bg-neutral/5',
        isInRange &&
          'bg-white before:pointer-events-none before:absolute before:inset-0 before:bg-primary/10',
        !isInRange && 'bg-space',
        showHShadow === 'left' && 'after:opacity-100',
        showHShadow === 'right' && 'after:opacity-100',
        showHShadow === false && 'after:opacity-0',
        !isPinned && 'z-0',
        isPinnedLeft && [
          'sticky left-(--column-start) z-30 bg-white',
          'after:pointer-events-none after:absolute after:top-0 after:right-[-5px] after:h-full after:w-[5px] after:bg-linear-to-l after:from-transparent after:to-neutral/10 after:transition-opacity after:duration-200',
        ],
        isPinnedRight && [
          'sticky right-(--column-after) z-30 bg-white',
          'after:pointer-events-none after:absolute after:top-0 after:left-[-5px] after:z-40 after:h-full after:w-[5px] after:bg-linear-to-r after:from-transparent after:to-neutral/10 after:transition-opacity after:duration-200',
        ],
        originalColumn?.onClick && 'cursor-pointer',
      )}
      style={{
        ...pinningStyles,
        width: cell.column.getSize(),
        transform: CSS.Translate.toString(transform),
      }}
      onClick={originalColumn?.onClick ? handleClick : undefined}
      onDoubleClick={
        originalColumn?.onDoubleClick ? handleDoubleClick : undefined
      }
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
};
